import React, { useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Toast, ToastContainer } from "react-bootstrap";
import { getApiErrorMessage } from "../../../store/api/baseApi";
import { useCreateRequestMutation } from "../../../store/api/requestsApi";
import "./RequestForm.css";

interface ConsultationFormData {
  name: string;
  phone: string;
  city: string;
  email: string;
}

const ConsultationForm: React.FC = () => {
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState<ConsultationFormData>({
    name: "",
    phone: "",
    city: "",
    email: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    try {
      await createRequest({
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        email: formData.email,
        message: `Interested in construction services in ${formData.city}`,
      }).unwrap();
      setErrorMessage("");
      setShowSuccessToast(true);
      setFormData({
        name: "",
        phone: "",
        city: "",
        email: "",
      });
      setValidated(false);
    } catch (apiError) {
      setErrorMessage(getApiErrorMessage(apiError as FetchBaseQueryError));
      setShowErrorToast(true);
    }
  };

  return (
    <>
      <div className='container'>
        <div className='row justify-content-center'>
          <div className='card shadow-lg border-0 consultation-card'>
            <div className='card-body p-4'>
              <h4 className='fw-bold text-center mb-4 consultation-title'>
                Speak With Our Team
              </h4>
              <form
                noValidate
                className={validated ? "was-validated" : ""}
                onSubmit={handleSubmit}
              >
                <div className='mb-3'>
                  <input
                    type='text'
                    name='name'
                    value={formData.name}
                    onChange={handleChange}
                    className='form-control'
                    placeholder='Enter your name'
                    minLength={2}
                    required
                  />
                  <div className='invalid-feedback'>
                    Please enter your name.
                  </div>
                </div>

                <div className='mb-3'>
                  <input
                    type='tel'
                    name='phone'
                    maxLength={10}
                    minLength={10}
                    pattern='[0-9]{10}'
                    value={formData.phone}
                    onChange={handleChange}
                    className='form-control'
                    placeholder='Enter your phone number'
                    required
                  />
                  <div className='invalid-feedback'>
                    Enter a valid 10-digit phone number.
                  </div>
                </div>

                <div className='mb-3'>
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    className='form-control'
                    placeholder='Email (optional — for confirmation)'
                  />
                </div>

                <div className='mb-3'>
                  <select
                    name='city'
                    value={formData.city}
                    onChange={handleChange}
                    className='form-select'
                    title='Select project city'
                    required
                  >
                    <option value=''>Select Project City</option>
                    <option value='Lucknow'>Lucknow</option>
                    <option value='Barabanki'>Barabanki</option>
                  </select>
                  <div className='invalid-feedback'>Please select a city.</div>
                </div>

                <div className='d-grid mt-4'>
                  <button
                    type='submit'
                    className='btn consultation-btn btn-lg'
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting..." : "Request Free Consultation"}
                  </button>
                </div>

                <p className='small mt-3 text-center consultation-note'>
                  By submitting, you agree to our privacy policy and allow us to
                  use your information as outlined.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position='bottom-end' className='p-3'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg='success'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Sent</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            Success! Your request has been submitted.
          </Toast.Body>
        </Toast>
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={4000}
          autohide
          bg='danger'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Failed</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            {errorMessage ||
              "Unable to submit request right now. Please try again."}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ConsultationForm;
